// Desc: Navbar component for the website
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [activeItem, setActiveItem] = useState('/');
  const [logoLoaded, setLogoLoaded] = useState(false);

  useEffect(() => {
    // Get current path from window location
    setActiveItem(window.location.pathname);

    // Add scroll event listener
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    // Preload the logo image
    const logoImage = new Image();
    logoImage.src = `${process.env.PUBLIC_URL}/assets/images/switch-logo.png`;
    logoImage.onload = () => setLogoLoaded(true);
    logoImage.onerror = () => console.error("Logo failed to load");

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrolled]);

  const navbarVariants = {
    initial: { backgroundColor: 'rgba(0, 0, 0, 0.5)' },
    scrolled: { 
      backgroundColor: 'rgba(0, 0, 0, 0.95)',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)' 
    }
  };

  const logoVariants = {
    initial: { opacity: 0, x: -20 },
    animate: { opacity: 1, x: 0, transition: { duration: 0.5 } }
  };

  const menuItemVariants = {
    initial: { opacity: 0, y: -10 },
    animate: index => ({
      opacity: 1,
      y: 0,
      transition: { duration: 0.3, delay: 0.1 * index }
    }),
    hover: { scale: 1.1, transition: { duration: 0.2 } }
  };

  const buttonVariants = {
    initial: { opacity: 0, scale: 0.8 },
    animate: { 
      opacity: 1, 
      scale: 1, 
      transition: { delay: 0.7, duration: 0.3 } 
    },
    hover: { 
      scale: 1.05, 
      backgroundColor: '#ff5347',
      transition: { duration: 0.2, type: 'spring', stiffness: 400 } 
    }
  };

  const menuItems = [
    { to: '/', label: 'Home' },
    { to: '/superapp', label: 'SuperApp' },
    { to: '/business', label: 'Business' },
    { to: '/about', label: 'About' },
    { to: '/careers', label: 'Careers' },
    { to: '/contact', label: 'Contact' }
  ];

  const SwitchLogoSVG = () => (
    <svg width="120" height="30" viewBox="0 0 120 30" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M19.7 8.4C25.1 8.4 28 10.5 28 15.2C28 19.9 25.1 22 19.7 22H12.3V8.4H19.7ZM19.5 18.5C21.6 18.5 22.9 17.5 22.9 15.2C22.9 12.9 21.6 11.9 19.5 11.9H17.4V18.5H19.5Z" fill="#FFFFFF"/>
      <path d="M41.2 12.5H36.3V14.9H40.7V18.3H36.3V22H31.2V9.1H41.2V12.5Z" fill="#FFFFFF"/>
      <path d="M53.3 9.1L48.7 22H43.1L38.5 9.1H44.1L46.2 16.8L48.3 9.1H53.3Z" fill="#FFFFFF"/>
      <path d="M55.3 9.1H60.4V22H55.3V9.1Z" fill="#00FFFF"/>
      <path d="M73 22H68.6L68.5 21.1C67.9 21.8 66.7 22.3 65.2 22.3C62.6 22.3 60.8 20.9 60.8 18.3C60.8 15.3 63.1 14.2 66.6 14.2H68.1V13.8C68.1 13 67.5 12.5 66.2 12.5C65 12.5 63.7 12.9 62.4 13.6L61 10.6C62.7 9.5 64.9 8.9 67 8.9C70.7 8.9 73 10.6 73 13.8V22ZM68.1 16.9H67.2C65.9 16.9 65.2 17.3 65.2 18.2C65.2 18.9 65.7 19.4 66.4 19.4C67.1 19.4 67.8 19 68.1 18.5V16.9Z" fill="#FFFFFF"/>
      <path d="M85.5 22H81.1L81 21.1C80.4 21.8 79.2 22.3 77.7 22.3C75.1 22.3 73.3 20.9 73.3 18.3C73.3 15.3 75.6 14.2 79.1 14.2H80.6V13.8C80.6 13 80 12.5 78.7 12.5C77.5 12.5 76.2 12.9 74.9 13.6L73.5 10.6C75.2 9.5 77.4 8.9 79.5 8.9C83.2 8.9 85.5 10.6 85.5 13.8V22ZM80.6 16.9H79.7C78.4 16.9 77.7 17.3 77.7 18.2C77.7 18.9 78.2 19.4 78.9 19.4C79.6 19.4 80.3 19 80.6 18.5V16.9Z" fill="#FFFFFF"/>
      <path d="M98.1 15.5C98.1 19.5 95.2 22.3 90.9 22.3C86.6 22.3 83.7 19.5 83.7 15.5C83.7 11.5 86.6 8.8 90.9 8.8C95.2 8.8 98.1 11.5 98.1 15.5ZM88.8 15.5C88.8 17.3 89.7 18.4 90.9 18.4C92.1 18.4 93 17.3 93 15.5C93 13.7 92.1 12.6 90.9 12.6C89.7 12.6 88.8 13.7 88.8 15.5Z" fill="#FFFFFF"/>
    </svg>
  );

  return (
    <motion.nav 
      className="navbar" 
      variants={navbarVariants}
      initial="initial"
      animate={scrolled ? "scrolled" : "initial"}
      transition={{ duration: 0.3 }}
    >
      <div className="navbar-container">
        <motion.div 
          className="navbar-logo"
          variants={logoVariants}
          initial="initial"
          animate="animate"
        >
          <Link to="/">
            {logoLoaded ? (
              <img 
                src={`${process.env.PUBLIC_URL}/assets/images/switch-logo.png`} 
                alt="Switch Logo" 
                onError={(e) => {
                  e.target.style.display = 'none';
                  setLogoLoaded(false);
                }}
              />
            ) : (
              <SwitchLogoSVG />
            )}
          </Link>
        </motion.div>
        <div className="navbar-menu">
          <ul className="menu-items">
            {menuItems.map((item, index) => (
              <motion.li 
                key={item.to} 
                className={`menu-item ${activeItem === item.to ? 'active' : ''}`}
                custom={index}
                variants={menuItemVariants}
                initial="initial"
                animate="animate"
                whileHover="hover"
              >
                <Link to={item.to} onClick={() => setActiveItem(item.to)}>
                  {item.label}
                  {activeItem === item.to && (
                    <motion.div 
                      className="active-indicator"
                      layoutId="activeIndicator"
                      style={{
                        position: 'absolute',
                        bottom: '-4px',
                        left: 0,
                        right: 0,
                        height: '2px',
                        backgroundColor: '#00ffff',
                      }}
                    />
                  )}
                </Link>
              </motion.li>
            ))}
          </ul>
        </div>
        <motion.div 
          className="login-button"
          variants={buttonVariants}
          initial="initial"
          animate="animate"
          whileHover="hover"
          whileTap={{ scale: 0.95 }}
        >
          <button>Log In</button>
        </motion.div>
      </div>
    </motion.nav>
  );
};

export default Navbar;